import Api, { ErrorResponse } from '@sancare/typescript-commons/src/misc/api'

import { refreshDisplayedSmrStay } from '@/misc/stayDocumentsMerge'
import { state } from '@/store/modules/stay/smr/state'
import { DependenciesType, RawSmrStay } from '@/store/modules/stay/smr/types'
import { StayStatusEnum } from '@/store/modules/stay/types'

import { ModeEnum, PmsiTypeEnum } from '../../settings/types'

export const actions = {
  fetchStay({ commit }, { idType, stayId }) {
    return Api.request(
      state.requests.fetchStay,
      {
        urlParams: { idType, stayId },
        successMut: 'fetchStaySuccess',
      },
      commit
    )
  },
  fetchRhs({ commit }, rhsId: number) {
    return Api.request(
      state.requests.fetchRhs,
      {
        urlParams: { id: rhsId },
        successMut: 'fetchRhsSuccess',
      },
      commit
    )
  },
  updateRhsDependency({ commit }, payload: { rhsId: number, dependencyType: DependenciesType, value: number }) {
    return Api.request(
      state.requests.updateRhsDependency,
      {
        urlParams: { id: payload.rhsId },
        body: {
          dependencyType: payload.dependencyType,
          value: payload.value
        },
        startMut: 'startRefreshingGroupingResult',
        successMut: 'updateDependenciesSuccess',
        errorMut: (response) => (response),
      },
      commit
    )
  },
  updateStayStatus({ commit, rootState }, { stayId, status, mode, pmsiType }: { stayId: string, status: StayStatusEnum, mode: ModeEnum, pmsiType: PmsiTypeEnum }) {
    return Api.request(
      state.requests.updateStayStatus,
      {
        body: { stayId, status, mode, pmsiType },
        // Documents were dropped from stay_smr_full: re-merge them in, since updateStayStatusSuccess
        // alone would otherwise leave the displayed stay's reports/textualHealthEntries content-less.
        successMut: async (stay: RawSmrStay) => {
          const previousStay = state.currentStay
          commit('updateStayStatusSuccess', stay)
          const rootCommit = (mutation: string, payload?: unknown) => commit(mutation, payload, { root: true })
          // refreshDisplayedSmrStay handles its own errors (toast + content-less fallback).
          await refreshDisplayedSmrStay(stay, rootCommit, rootState.stayList.currentSearch, rootState.settings.appSettings, previousStay)
        },
        // Unlike the legacy @/misc/api used on the MCO side, Api.request doesn't toast on error:
        // surface the backend's message (e.g. a RHS without MMP refusing the validation).
        errorMut: (error: ErrorResponse) => commit('toast/setError', error.message, { root: true }),
      },
      commit
    )
  },
  commentStay({ commit }, { comment, stayId }) {
    return Api.request(
      state.requests.commentStay,
      {
        urlParams: { id: stayId },
        body: {
          comment
        },
        successMut: 'commentStaySuccess',
      },
      commit
    )
  },
  // Used by backend-driven popins (ApiJustificationSummary.vue via flag-justification), where the
  // label (type+reference) is already known unambiguously.
  createFalseJustificationForSuggestion({ commit }, { errorType, docType, docId, hintStart, hintEnd, labelType, labelReference }) {
    return Api.request(
      state.requests.createFalseJustificationForSuggestion,
      {
        urlParams: { stayId: state.currentStay.id },
        body: { errorType, docType, docId, hintStart, hintEnd, labelType, labelReference },
        successMut: 'createFalseJustificationsSuccess',
      },
      commit
    )
  },
  deleteFalseJustification({ commit }, { justificationId, reportId, healthEntryId }) {
    return Api.request(
      state.requests.deleteFalseJustification,
      {
        urlParams: { id: justificationId },
        successMut: () => commit('deleteFalseJustificationsSuccess', { justificationId, reportId, healthEntryId }),
      },
      commit
    )
  },
}
